/**
 * The password platform staff generate for a client's operator.
 *
 * It is read off a screen and passed on by hand, so the alphabet leaves out the
 * characters people misread: `0`/`O`/`o`, and `1`/`l`/`I`. Fifty-six remain,
 * and twenty of them carry about 116 bits — far beyond the twelve-character
 * floor the server enforces (`contracts/src/identity.ts`), which is the only
 * policy there is. The server still validates what arrives; this module decides
 * nothing about acceptability.
 *
 * **Rejection sampling, not modulo.** A random byte has 256 values and 256 is
 * not a multiple of 56, so `byte % 56` would make the first 32 characters
 * slightly likelier than the rest. Bytes at or above the largest multiple of the
 * alphabet size (224) are discarded instead, which leaves every character
 * exactly equally likely.
 *
 * Kept inside the console rather than imported from `contracts`, because the
 * console imports nothing internal (CLAUDE.md).
 */

export const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

export const GENERATED_PASSWORD_LENGTH = 20;

/** Fills a buffer with cryptographically secure bytes. Injected only by tests. */
export type RandomFill = (buffer: Uint8Array) => void;

const secureFill: RandomFill = (buffer) => {
  globalThis.crypto.getRandomValues(buffer);
};

/** The first byte value that would bias the draw, and so is thrown away. */
const REJECT_FROM = 256 - (256 % PASSWORD_ALPHABET.length);

/**
 * Bytes requested per draw. More than twenty, so a draw that loses a few to
 * rejection (about one byte in eight) usually still finishes in one call.
 */
const BATCH = 32;

export const generatePassword = (
  length: number = GENERATED_PASSWORD_LENGTH,
  fill: RandomFill = secureFill,
): string => {
  let password = '';
  while (password.length < length) {
    const bytes = new Uint8Array(BATCH);
    fill(bytes);
    for (const byte of bytes) {
      if (byte >= REJECT_FROM || password.length === length) continue;
      password += PASSWORD_ALPHABET[byte % PASSWORD_ALPHABET.length];
    }
  }
  return password;
};
